import { useEffect, useRef } from 'react';
import {
  mcpPayloadToOutputText,
  normalizeMcpCallSocketData,
} from '../../../../util/mcpCallSocketEnvelope';
import { subscribeMcpCallSocket } from '../../../../util/mcpCallSocketBridge';
import { useSocketStore } from '../../../../store/storeSocket';

export interface McpSocketResolvedPayload {
  text: string;
  isError: boolean;
  errorMessage?: string;
}

export function useMcpSocketResponse(
  nodeId: string,
  onResolved: (payload: McpSocketResolvedPayload) => void,
  clearResponseTimeout: () => void,
) {
  const pendingClientRequestIdRef = useRef<string | null>(null);
  const onResolvedRef = useRef(onResolved);
  const clearTimeoutRef = useRef(clearResponseTimeout);
  onResolvedRef.current = onResolved;
  clearTimeoutRef.current = clearResponseTimeout;

  useEffect(() => {
    return subscribeMcpCallSocket(msg => {
      const raw = msg?.data;
      const hadEnvelope =
        raw !== null &&
        typeof raw === 'object' &&
        !Array.isArray(raw) &&
        'mcpPayload' in raw;
      const norm = normalizeMcpCallSocketData(raw);
      if (hadEnvelope) {
        if (norm.clientRequestId) {
          if (norm.clientRequestId !== pendingClientRequestIdRef.current) {
            return;
          }
        } else if (pendingClientRequestIdRef.current) {
          return;
        }
        if (norm.nodeId && norm.nodeId !== nodeId) {
          return;
        }
      } else {
        if (pendingClientRequestIdRef.current) {
          return;
        }
        const active = useSocketStore.getState().activeNodeSockets;
        if (!active.includes(nodeId)) {
          return;
        }
      }
      clearTimeoutRef.current();
      const parsed = mcpPayloadToOutputText(norm.payload);
      onResolvedRef.current(parsed);
      pendingClientRequestIdRef.current = null;
    });
  }, [nodeId]);

  return { pendingClientRequestIdRef };
}
